<template>
    <div
    v-if="!showPatrimonyLoader && !simplifiedVersion"
    class="patrimony-tab-content-wrapper"
    :class="{ mobile: getMobileAppState }">
        <tab-segment :tabsList="patrimonyTabsList" @clicked="tabClickedHandler"/>
        <!-- Chart -->
        <chart-base
        v-if="!showEmptyState"
        :key="selectedTab"
        :selectedTab="selectedTab"
        :variant="variant"
        :isMobile="getMobileAppState"
        @showEmptyState="showEmptyState = true" />
        <!-- Empty-State -->
        <empty-state-generic
        v-else
        class="patrimony-empty-state"
        illustration="emptyState/patrimony"
        :key="`emptyState${selectedTab}`"
        :title="emptyStateTitle"
        :description="emptyStateDescription"/>
    </div>
    <patrimony-simplified
    v-if="!showPatrimonyLoader && simplifiedVersion"
    :hideAmounts="hideAmounts"/>
    <!-- Loader Generic -->
    <loaders-generic v-if="showPatrimonyLoader" fullSize/>
</template>

<script setup>
import TabSegment from '@/components/generic/tabs/Segment'
import ChartBase from '@/components/features/home/widgets/patrimonyTabChart/Base'
import PatrimonySimplified from '@/components/features/home/widgets/patrimonyTabChart/PatrimonySimplified.vue'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'

/* Vue & Assets */
import { ref, computed, watch } from 'vue'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Props
@showPatrimonyLoader {Boolean} - prop to tell widget patrimony fetch state
@simplifiedVersion {Boolean} - prop to apply simplified version
@variant {String} - can be [ none (default), anchor]
*/
const props = defineProps({
    showPatrimonyLoader: Boolean,
    simplifiedVersion: Boolean,
    hideAmounts: Boolean,
    variant: String
})

/* mapGetters */
const { getMobileAppState } = mapGetters()

/*************************************************
*                                                *
*                       Tabs                     *
*                                                *
*************************************************/
const patrimonyTabsList = ref([{
    id: 'assets',
    label: t('home.widgets.patrimony.tabs.assets'),
    selected: true
},
{
    id: 'liabilities',
    label: t('home.widgets.patrimony.tabs.liabilities'),
    selected: false
},
{
    id: 'assetsVsLiabilities',
    label: t('home.widgets.patrimony.tabs.assetsVsLiabilities'),
    selected: false
}])

const tabClickedHandler = event => {
    patrimonyTabsList.value.forEach(tab => {
		tab.selected = tab.id === event
	})
}

const selectedTab = computed(() => {
    return patrimonyTabsList.value.find(obj => obj.selected).id
})

/*************************************************
*                                                *
*                   Empty State                  *
*                                                *
*************************************************/
const showEmptyState = ref(false)

const emptyStateTitle = computed(() => {
    switch (selectedTab.value) {
        case 'assets':
            return t('home.widgets.patrimony.emptyStates.emptyTitleAssets')
        case 'liabilities':
            return t('home.widgets.patrimony.emptyStates.emptyTitlePassives')
        default:
            return t('home.widgets.patrimony.emptyStates.emptyTitleAssetsVsLiabilities')
    }
})
const emptyStateDescription = computed(() => {
    switch (selectedTab.value) {
        case 'assets':
            return t('home.widgets.patrimony.emptyStates.emptyTextAssets')
        case 'liabilities':
            return t('home.widgets.patrimony.emptyStates.emptyTextPassives')
        default:
            return t('home.widgets.patrimony.emptyStates.emptyTextAssetsVsLiabilities')
    }
})

watch(() => selectedTab.value, () => {
    showEmptyState.value = false
})
</script>

<style lang="scss" scoped>
.patrimony-tab-content-wrapper {
    padding: 16px 24px 0 24px;

    &.mobile {
        display: flex;
        flex-direction: column;
        align-items: center;

        padding: 0;

        .tabs-segment-wrapper {
            margin-top: 24px;
        }
    }
}
</style>
